/**
 * プラン上限接近・到達時のバナー UI.
 *
 * 表示ロジック:
 *   - current / limit >= 0.8 (80%) で warning バナー
 *   - current / limit >= 1.0 (100%) で blocking バナー (赤) + アップグレード CTA
 *   - limit が 'unlimited' (= -1) のときは何も表示しない
 *
 * 使用例:
 *   <PlanLimitBanner resource="posts" current={97} limit={100} planId="free" />
 */

import type { ReactNode } from 'react';

export type PlanResourceLabel =
  | 'sites'
  | 'posts'
  | 'media'
  | 'members'
  | 'webhooks'
  | 'custom_domains';

const RESOURCE_LABEL_JA: Record<PlanResourceLabel, string> = {
  sites: 'サイト数',
  posts: '記事数',
  media: '画像容量',
  members: 'メンバー数',
  webhooks: 'Webhook 数',
  custom_domains: 'カスタムドメイン数',
};

interface PlanLimitBannerProps {
  resource: PlanResourceLabel;
  /** 現在値 (media の場合 bytes) */
  current: number;
  /** プラン上限 (-1 / 'unlimited' は制限なし) */
  limit: number | 'unlimited';
  /** 現在のプラン ID */
  planId: string;
  /** 課金画面への遷移コールバック (未指定ならボタン非表示) */
  onUpgradeClick?: () => void;
  /** 追加の説明テキスト */
  hint?: ReactNode;
}

function formatValue(resource: PlanResourceLabel, value: number): string {
  if (resource !== 'media') return String(value);
  // media は bytes → GB / MB
  const mb = value / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)}GB`;
  return `${mb.toFixed(0)}MB`;
}

export function PlanLimitBanner({
  resource,
  current,
  limit,
  planId,
  onUpgradeClick,
  hint,
}: PlanLimitBannerProps) {
  // unlimited なら非表示
  if (limit === 'unlimited' || limit === -1) return null;
  if (typeof limit !== 'number' || limit <= 0) return null;

  const ratio = current / limit;
  if (ratio < 0.8) return null;

  const blocking = ratio >= 1.0;
  const label = RESOURCE_LABEL_JA[resource];
  const currentStr = formatValue(resource, current);
  const limitStr = formatValue(resource, limit);

  const tone = blocking
    ? {
        bg: 'bg-[#fef2f2]',
        border: 'border-[#fecaca]',
        text: 'text-[#b91c1c]',
        btn: 'bg-[#dc2626] hover:bg-[#b91c1c] text-white',
        icon: '●',
      }
    : {
        bg: 'bg-[#fffbeb]',
        border: 'border-[#fde68a]',
        text: 'text-[#92400e]',
        btn: 'bg-[#f59e0b] hover:bg-[#d97706] text-white',
        icon: '▲',
      };

  return (
    <div
      role={blocking ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-[8px] border px-4 py-3 text-sm ${tone.bg} ${tone.border} ${tone.text}`}
      data-plan-limit-banner={resource}
      data-blocking={blocking ? '1' : '0'}
    >
      <span aria-hidden className="mt-0.5 leading-none">
        {tone.icon}
      </span>
      <div className="flex-1">
        <p className="font-medium">
          {blocking
            ? `${label}の上限に達しました（${currentStr} / ${limitStr}）`
            : `${label}の上限が近づいています（${currentStr} / ${limitStr}）`}
        </p>
        {hint && <p className="mt-1 text-xs opacity-80">{hint}</p>}
        <p className="mt-1 text-xs opacity-80">
          現在のプラン: <code className="font-mono">{planId}</code>
        </p>
      </div>
      {onUpgradeClick && (
        <button
          type="button"
          onClick={onUpgradeClick}
          className={`shrink-0 rounded-[6px] px-3 py-1.5 text-xs font-medium transition ${tone.btn}`}
        >
          プランを見直す
        </button>
      )}
    </div>
  );
}
